import * as React from 'react';
import { IconType } from '@repo/ui/components';

export type CartImageData = {
  altText?: string;
  url: string;
  defaultUrl?: string;
};

export type CartHeaderProps = {
  make: string;
  modelSeries: string;
  image?: CartImageData;
  onOpenEquipmentList?: () => void;
  showEquipmentListLink?: boolean;
  headerChildren?: React.ReactNode;
  showVehicleImage?: boolean;
  isUsedImportCar?: boolean;
};

export type CartSummaryData = {
  key: string;
  id: string;
  name: string;
  price: number;
  isRecommended?: boolean;
  vatCode?: string;
  retailPrice?: number;
  percentage?: string;
};

export type CartPrices = {
  salesTotalExcludingVatAndTax: number;
  electrifiedCarVatCalculationBasis: number;
  calculatedVatOnDeductableItems: number;
  calculatedVatOnNonDeductableItems: number;
  oneOffTaxAndWreckage: number;
  calculatedSalesTotalBeforeDiscount: number;
  discount: number;
  salesTotalIncludingVatAndTax: number;
  calculatedVatOnSalesTotal: number;
  calculatedDiscountOnVehicle: number;
  calculatedDiscountOnAccessories: number;
  calculatedDiscountOnDeliveryCost: number;
  calculatedDiscountOnVehicleExclVat?: number;
  calculatedDiscountOnAccessoriesExclVat?: number;
  calculatedDiscountOnDeliveryCostExclVat?: number;
  totalPriceExclVatInclTaxDiscount?: number;
};

export type EquipmentDetailsProps = {
  key?: string;
  title: string;
  value: string;
  vatCode?: string;
};

export type OneOffTaxAndWreckage = {
  isEditable: boolean;
  taxAndWreckageValue: number;
  resetTaxAndWreckageValue: number;
  inputValue: (param: number, doneReset: boolean) => void;
};

export type CartLocationProps = {
  id: string;
  name: string;
  isSelected: boolean;
};

export type CartStatusProps = {
  id: string;
  name: string;
  isSelected: boolean;
};

export type CartSummaryProps = {
  model?: string;
  reRegistrationFee?: number;
  totalPrice?: number;
  accessoryTax?: number;
  onReRegistrationFeeChange?: (fee: number) => void;
  onTotalPriceChange?: (price: number) => void;
  onOneOffTaxChange?: (tax: number) => void;
  isUsedImportCar?: boolean;
  oneOffTaxAndWreckage?: number;
  prices: CartPrices;
  data?: CartSummaryData[];
  isElectricCar?: boolean;
  equipmentDetails?: EquipmentDetailsProps[];
  oneOffTaxAndWreckageDetails?: OneOffTaxAndWreckage;
  rammeavtale?: boolean;
  bruksKodeTag?: {
    isTagVisible: boolean;
    label: string | React.ReactNode;
    onTagClick?: () => void;
  };
  oneOffTaxChildren?: React.ReactNode;
};

export enum ReserveButtonStages {
  NO_RESERVE_REQUEST = 0,
  WAITING = 1,
  RESERVED = 2,
}

export type CancelFactoryOrder = {
  buttonAction: () => void;
  buttonLabel: string;
};

type VehicleCountBySeller = {
  own: {
    own?: number;
  };
  port: {
    importer?: number;
  };
  other: {
    dealer?: number;
  };
};

export type CartFooterProps = {
  isShowFooter?: boolean;
  isVehicleSelected?: boolean;
  serialNo?: number;
  isFactoryOrder?: boolean;
  isAllowOffer?: boolean;
  vehicleCount?: number;
  location?: CartLocationProps[];
  status?: CartStatusProps[];
  selectedLocation?: string;
  selectedStatus?: string;
  vehicleTTCode?: number;
  isFactoryOrderEnable?: boolean;
  reserveButtonStage?: ReserveButtonStages;
  isFilterAllow?: boolean;
  isSelectedVehicleUnavailable?: boolean;
  expectedProductionDate?: Date;
  onOpenVehicleList?(key?: string): void;
  onReserveVehicle?(): void;
  onSaveOfferPDF?(): void;
  onSelectLocation?(id: string, value: boolean): void;
  onSelectStatus?(name: string, value: boolean): void;
  onSaveFactoryOrder?(prodDate: string): void;
  isLoading?: boolean;
  showReserveButton?: boolean;
  showEquipmentListLink?: boolean;
  vehicleCountBySeller?: VehicleCountBySeller;
  onSellerChangeByCount?(id: string): void;
  showChangeSpecificCarButton?: boolean;
  isProdDateDisabled?: boolean;
  footerChildren?: React.ReactNode;
  exactMatchCount?: number;
  nonExactMatchCount?: number;
  showAbortFactoryOrderAction?: boolean;
  abortFactoryBorder?: CancelFactoryOrder;
  equipmentDetails?: EquipmentDetailsProps[];
  reservationInfoText?: string;
  reservationToDateLabel?: string;
  showReservationInfo?: boolean;
  isFactoryOrderButtonDisabled?: boolean;
  isIFSReservationInProgress?: boolean;
  orderType?: string;
};

export type CartProps = CartHeaderProps & CartSummaryProps & CartFooterProps;

export type TFactoryOrderSection = {
  expectedProductionDate?: Date;
  onSaveFactoryOrder?(prodDate: string): void;
  setIsFactoryOrderOpen(status: boolean): void;
  isLoading?: boolean;
  isProdDateDisabled?: boolean;
};

export type ReserveButtonProps = {
  isVehicleReserved: boolean;
  isReservationInWaiting: boolean;
  isSelectedVehicleUnavailable: boolean;
  onReserveVehicle?(): void;
  isLoading?: boolean;
  reservationInfoText?: string;
};

export type TFactoryOrderButton = {
  isFactoryOrderOpen?: boolean;
  isVehicleReserved?: boolean;
  isReservationInWaiting: boolean;
  isDisabled?: boolean;
  setIsFactoryOrderOpen(status: boolean): void;
};
